import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from '../config/api';
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  User, 
  Building2, 
  FileText, 
  PoundSterling,
  Clock,
  Scale,
  CalendarIcon,
  Loader2,
  FlaskConical,
  Mail,
  Users,
  Plus,
  Trash2,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Lock,
  KeyRound,
  Search
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { AIMatterSummary } from "../components/AIAssistant";
import { InfoTooltip } from "../components/InfoTooltip";

import { LOGO_URL } from "../config/constants";

// Matter Types for CCL template selection
// Grouped by Practice Area Code for clarity
const MATTER_TYPES = [
  // 001 – Residential
  { value: "purchase_resi", label: "Residential Purchase", code: "001" },
  { value: "sale_resi", label: "Residential Sale", code: "001" },
  { value: "remortgage", label: "Remortgage", code: "001" },
  { value: "remortgage_toe", label: "Remortgage + Transfer of Equity", code: "001" },
  { value: "toe_only", label: "Transfer of Equity Only", code: "001" },
  { value: "auction_review", label: "Auction Pack Review", code: "001" },
  { value: "declaration_trust", label: "Declaration of Trust", code: "001" },
  // 002 – Corporate/Commercial
  { value: "contracts", label: "Contracts (Commercial)", code: "002" },
  { value: "apa_purchase", label: "APA – Business / Asset Purchase", code: "002" },
  { value: "spa_purchase", label: "SPA – Share Purchase (Buyer)", code: "002" },
  { value: "spa_sale", label: "SPA – Share Sale (Seller)", code: "002" },
  { value: "ila", label: "Independent Legal Advice (ILA)", code: "002" },
  { value: "mbi", label: "Management Buy-In (MBI)", code: "002" },
  { value: "mbo", label: "Management Buy-Out (MBO)", code: "002" },
  { value: "shareholders_cross_option", label: "Shareholders Agreement & Deed of Cross-Option", code: "002" },
  // 003 – Commercial Property
  { value: "comm_lease_assgn_surr", label: "Commercial Lease – Assignment / Surrender", code: "003" },
  { value: "comm_freehold_purchase", label: "Commercial Freehold Purchase", code: "003" },
  { value: "comm_freehold_sale", label: "Commercial Freehold Sale", code: "003" },
  { value: "comm_lease_landlord", label: "Commercial Lease — Landlord", code: "003" },
  { value: "comm_lease_tenant", label: "Commercial Lease — Tenant", code: "003" },
  { value: "comm_lease_assignment", label: "Commercial Lease — Assignment", code: "003" },
  { value: "comm_lease_surrender", label: "Commercial Lease — Surrender", code: "003" },
  // 006 – Disputes/Litigation
  { value: "disputes", label: "Dispute (Contentious)", code: "006" },
  // 007 – Intellectual Property
  { value: "ip_trademark", label: "Trademark", code: "007" },
  { value: "ip_copyright", label: "Copyright", code: "007" },
  { value: "ip_patent", label: "Patent", code: "007" }
];

const STEPS = [
  { id: 1, title: "Client Info", icon: User },
  { id: 2, title: "Matter Type", icon: FileText },
  { id: 3, title: "Conflict Check", icon: Shield },
  { id: 4, title: "Matter Details", icon: Building2 },
  { id: 5, title: "Pricing", icon: PoundSterling },
  { id: 6, title: "Scope", icon: Scale },
  { id: 7, title: "Review", icon: Check }
];

// Conditional field visibility rules
const FIELD_VISIBILITY = {
  Q8_property_address: ["purchase_resi", "sale_resi", "comm_lease_assgn_surr", "remortgage", "remortgage_toe", "toe_only", "declaration_trust", "auction_review"],
  Q8_seller_name: ["purchase_resi", "apa_purchase"],
  Q8_buyer_name: ["sale_resi", "spa_purchase"],
  Q8_tenant_name: [],
  Q8_landlord_name: ["comm_lease_assgn_surr"],
  Q8_target_company: ["spa_purchase", "spa_sale", "mbi", "mbo"],
  Q8_seller_business_name: ["apa_purchase"],
  Q8_new_lender: ["remortgage", "remortgage_toe"],
  Q8_existing_lender: ["remortgage", "remortgage_toe"],
  Q8_new_owner: ["toe_only", "remortgage_toe"],
  Q8_auction_house: ["auction_review"],
  Q8_company_name_shareholders: ["shareholders_cross_option"],
  Q8_current_owner_name: ["mbi", "mbo"],
  tenure: ["purchase_resi", "sale_resi"],  // Freehold or Leasehold
  completion_date: ["purchase_resi", "sale_resi"],  // Expected completion date
  Q24_number_of_shares: ["spa_purchase", "spa_sale"],
  Q25_percentage_shareholding: ["spa_purchase", "spa_sale"],
  Q27_business_name_contracts: ["contracts"],
  Q27_contract_scope: ["contracts"],
  Q27_scope1: ["contracts"],
  Q27_scope2: ["contracts"],
  Q27_scope3: ["contracts"],
  Q27_scope4: ["contracts"],
  Q18_timescale_searches: ["purchase_resi"],
  Q18_estimated_timescale: ["purchase_resi", "sale_resi", "other_general"],
  Q18_timescale_combined: ["remortgage_toe"],
  Q18_timescale_review: ["auction_review"],
  // Other / General CCL specific fields
  Q28_general_description: ["other_general"],
  Q29_legal_work: ["other_general"],
  Q30_fee_earner_position: ["other_general"],
  Q31_backup_staff_email: ["other_general"],
  Q32_backup_staff_position: ["other_general"],
};

export default function NewMatter() {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    submitter_name: "",
    submitter_email: "",
    Q1: "",
    Q2: "",
    Q3: "",
    Q4: "",
    Q5: "",
    Q6: "",
    Q7: "None",
    Q9: "",
    Q10: "",
    Q11: "Private individual",
    Q12: "Yes",
    Q13: "Fixed fee",
    Q14: "",
    Q15: "50% on outset, 50% on exchange/completion (default)",
    Q15_other: "",
    Q16: "",
    Q17: "No",
    Q17_amount: "",
    Q19: "",
    Q20: "",
    Q21: "No",
    Q29: null,
    Q30: "No",
    Q30_details: "",
    Q31: "",
    Q32A: "Yes – use standard wording",
    Q32C: "",
    Q32D: "",
    is_test_submission: false,
    // Other / General CCL
    Q28_general_description: "",
    Q29_legal_work: "",
    Q30_fee_earner_position: "",
    Q31_backup_staff_email: "",
    Q32_backup_staff_position: "",
    // Other side parties
    other_side_parties: [],
    no_counterparty: false,
    // SharePoint integration
    sharepoint_matter_type: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [standardScope, setStandardScope] = useState("");
  const [conflictCheck, setConflictCheck] = useState(null);
  const [conflictLoading, setConflictLoading] = useState(false);
  const [draftSubmissionId, setDraftSubmissionId] = useState(null);
  const [gating, setGating] = useState(null);
  const [matterRef, setMatterRef] = useState(null);
  const [ccSearchQuery, setCcSearchQuery] = useState("");
  const [ccSearchResults, setCcSearchResults] = useState([]);
  const [ccSearching, setCcSearching] = useState(false);
  // BM-H3864: declared here but read in Step1ClientInfo and Step7Review,
  // where the names did not exist. Passed down as props now.
  const [feeEarners, setFeeEarners] = useState([]);
  const [backupStaff, setBackupStaff] = useState([]);
  const navigate = useNavigate();

  // Fetch staff data from API
  useEffect(() => {
    axios.get(`${API}/staff`).then(res => {
      setFeeEarners(res.data.fee_earners.filter(s => s.role === "fee_earner" && s.department !== "Operations"));
      setBackupStaff(res.data.backup_staff);
    }).catch(() => {});
  }, []);

  // Load pre-population from OB if matter_reference or provisional_id is in URL
  useEffect(() => {
    const ref = searchParams.get('matter_reference') || searchParams.get('provisional_id');
    const conflictId = searchParams.get('conflict_id');
    const submissionId = searchParams.get('submission_id');
    
    if (ref) {
      setMatterRef(ref);
      loadFromOB(ref);
    } else if (conflictId) {
      // Auto-link conflict check when coming from Conflict Checks tab
      autoLinkConflict(conflictId, submissionId);
    }
  }, [searchParams]);

  const autoLinkConflict = async (conflictId, submissionId) => {
    try {
      setConflictLoading(true);
      // Fetch the conflict check directly by ID
      const res = await axios.get(`${API}/conflict-checks/${conflictId}`);
      if (res.data) {
        setConflictCheck(res.data);
        // If the conflict check has a matter_reference, load OB data too
        if (res.data.matter_reference || res.data.provisional_id) {
          const ref = res.data.matter_reference || res.data.provisional_id;
          setMatterRef(ref);
          loadFromOB(ref);
        }
        // Set submission ID for linking
        if (submissionId || res.data.submission_id) {
          setDraftSubmissionId(submissionId || res.data.submission_id);
        }
        // Skip to step 3 (Conflict Check) since it's auto-linked
        setCurrentStep(3);
      }
    } catch (err) {
      console.error("Failed to auto-link conflict check:", err);
      toast.error("Could not find the linked conflict check");
    } finally {
      setConflictLoading(false);
    }
  };

  const loadFromOB = async (provId) => {
    try {
      const res = await axios.get(`${API}/forf/pre-populate/${provId}`);
      const { forf_data, conflict, gating: gatingData } = res.data;
      setGating(gatingData);
      
      if (conflict) {
        setConflictCheck(conflict);
      }
      
      if (forf_data) {
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(forf_data).filter(([_, v]) => v !== null && v !== undefined && v !== "")
          ),
        }));
      }
    } catch (err) {
      console.error("Failed to load OB data:", err);
    }
  };

  // Check for conflict check completion from URL params
  useEffect(() => {
    const conflictDone = searchParams.get('conflict_done');
    const submissionIdParam = searchParams.get('submission_id');
    if (conflictDone === 'true' && submissionIdParam) {
      setDraftSubmissionId(submissionIdParam);
      fetchConflictStatus(submissionIdParam);
      // Load the submission data to restore form state
      loadSubmissionData(submissionIdParam);
      // Go to the review step
      setCurrentStep(7);
    }
  }, [searchParams]);

  // Load submission data to restore form state
  const loadSubmissionData = async (submissionId) => {
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}`);
      const submission = response.data;
      if (submission && submission.data) {
        // Restore form data from submission
        setFormData(prev => ({
          ...prev,
          ...submission.data
        }));
        // Fetch standard scope for the matter type
        if (submission.data.Q9) {
          fetchStandardScope(submission.data.Q9);
        }
      }
    } catch (error) {
      console.error("Failed to load submission data:", error);
    }
  };

  // Fetch standard scope when matter type changes
  useEffect(() => {
    if (formData.Q9) {
      fetchStandardScope(formData.Q9);
    }
  }, [formData.Q9]);

  const fetchStandardScope = async (matterType) => {
    try {
      const response = await axios.get(`${API}/lookup/standard-scope/${matterType}`);
      setStandardScope(response.data.scope);
    } catch (error) {
      console.error("Failed to fetch standard scope:", error);
      setStandardScope("");
    }
  };

  // CC gate search
  useEffect(() => {
    if (ccSearchQuery.length < 2) { setCcSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setCcSearching(true);
      try {
        const res = await axios.get(`${API}/conflict-checks/all`, { params: { search: ccSearchQuery, limit: 8 } });
        setCcSearchResults(res.data?.conflict_checks || []);
      } catch { setCcSearchResults([]); }
      setCcSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [ccSearchQuery]);

  const linkConflictCheck = (cc) => {
    setConflictCheck(cc);
    if (cc.matter_reference || cc.provisional_id) {
      const ref = cc.matter_reference || cc.provisional_id;
      setMatterRef(ref);
      loadFromOB(ref);
    }
    setCcSearchQuery("");
    setCcSearchResults([]);
    toast.success(`Linked conflict check: ${cc.matter_reference || cc.provisional_id || cc.client_name}`);
  };

  const ccPassStates = ["clear", "clear_with_conditions", "not_needed", "na"];
  const ccDeferredStates = ["deferred", "not_ready"];
  const isCcAcceptable = (cc) => ccPassStates.includes(cc.conflict_status) || ccDeferredStates.includes(cc.conflict_status) || cc.has_override;

  const fetchConflictStatus = async (submissionId) => {
    try {
      setConflictLoading(true);
      const response = await axios.get(`${API}/conflict-checks/by-submission/${submissionId}`);
      if (response.data.exists) {
        setConflictCheck(response.data.conflict_check);
      }
    } catch (error) {
      console.error("Failed to fetch conflict status:", error);
    } finally {
      setConflictLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isFieldVisible = (fieldId) => {
    if (!FIELD_VISIBILITY[fieldId]) return false;
    return FIELD_VISIBILITY[fieldId].includes(formData.Q9);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        // Q4 (Our Reference) is auto-generated in Step 2, so not required here
        return formData.submitter_name && formData.submitter_email && 
               formData.Q1 && formData.Q2 && formData.Q3 && formData.Q6;
      case 2:
        return formData.Q9 && formData.sharepoint_matter_type;
      case 3:
        // Conflict check must be linked
        return !!conflictCheck;
      case 4:
        // Validate Other/General CCL specific required fields
        if (formData.Q9 === "other_general") {
          if (!formData.Q28_general_description?.trim()) return false;
          if (!formData.Q29_legal_work?.trim() && !formData.Q3?.trim()) return false;
          if (!formData.Q30_fee_earner_position?.trim()) return false;
          if (!formData.Q31_backup_staff_email?.trim()) return false;
          if (!formData.Q32_backup_staff_position?.trim()) return false;
        }
        return true;
      case 5:
        return formData.Q13;
      case 6:
        return formData.Q32A;
      case 7:
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Golden Rule gating: block past step 3 if OB not complete
      if (currentStep === 3 && matterRef && gating && !gating.ob_complete) {
        toast.error("Client Onboarding (Stage 2) must be completed before you can proceed with the FORF. Current OB status: " + (gating.ob_status || "not started"));
        return;
      }
      setCurrentStep(prev => Math.min(prev + 1, 7));
    } else {
      if (currentStep === 3) {
        toast.error("Please link an existing conflict check before proceeding");
      } else {
        toast.error("Please complete all required fields");
      }
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Check if submission can proceed based on conflict check + matter type.
  // matter_type (Q9) is required: silent defaults route the matter into the
  // wrong pipeline (templates, fees, ID checks). Submit must stay disabled
  // until the user picks one explicitly.
  const canSubmit = () => {
    if (!formData.Q9) return false;
    if (!MATTER_TYPES.some(m => m.value === formData.Q9)) return false;
    if (!conflictCheck) return false;
    const status = conflictCheck.conflict_status;
    const hasOverride = conflictCheck.has_override;

    if (status === "clear" || status === "clear_with_conditions") return true;
    if (status === "do_not_act" && hasOverride) return true;
    return false;
  };

  const handleSubmit = async () => {
    // matter_type must be chosen — never silently default it.
    if (!formData.Q9 || !MATTER_TYPES.some(m => m.value === formData.Q9)) {
      toast.error("Cannot submit: a matter type must be explicitly selected (Step 2)");
      return;
    }
    // Check conflict check status before allowing submission
    if (!canSubmit()) {
      toast.error("Cannot submit: Conflict check must be completed with 'Clear' or 'Clear with conditions' status");
      return;
    }
    
    // Gate: OB must be complete or overridden
    if (gating && !gating.ob_complete) {
      toast.error("Cannot submit: Client Onboarding (Stage 2) must be completed before the FORF can be submitted");
      return;
    }
    
    setSubmitting(true);
    try {
      // Include matter_reference and gating info in the submission data
      const submitData = { 
        ...formData,
        matter_reference: matterRef || undefined,
        provisional_id: matterRef || undefined,
        thirdfort_status: gating?.thirdfort_status || undefined,
        thirdfort_complete: gating?.thirdfort_complete || false,
      };

      // BM-H Audit 07 P1 (01/09/2026): if the BE soft-refuses with the
      // PA-mismatch 409, prompt the user with both practice-area values
      // and re-submit with pa_mismatch_ack=true on confirm. Silent
      // divergence between the CC and the intake PA was the root cause
      // of the Reeken Patel re-mint work — the fee earner has to see
      // both values and consciously choose.
      const submitWithPaAck = async (payload) => {
        if (draftSubmissionId) {
          return await axios.put(`${API}/submissions/${draftSubmissionId}`, { data: payload });
        }
        return await axios.post(`${API}/submissions`, { data: payload });
      };

      const trySubmit = async (payload) => {
        try {
          return await submitWithPaAck(payload);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          const code = detail?.code || detail?.error;
          if (err?.response?.status === 409 && code === 'pa_mismatch_needs_ack') {
            const ccPa = detail?.conflict_check_practice_area || '(unknown)';
            const miPa = detail?.matter_intake_practice_area || '(unknown)';
            const client = detail?.client_name ? ` for ${detail.client_name}` : '';
            const proceed = window.confirm(
              `Practice area differs from conflict check${client}.\n\n` +
              `Conflict check: ${ccPa}\n` +
              `Matter intake:  ${miPa}\n\n` +
              `Proceed with the intake practice area? The CC will keep its own.`
            );
            if (!proceed) throw err;
            return await submitWithPaAck({ ...payload, pa_mismatch_ack: true });
          }
          throw err;
        }
      };

      const response = await trySubmit(submitData);
      toast.success("Matter intake submitted successfully!");
      navigate(`/submission/${draftSubmissionId || response?.data?.id}`);
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error("Failed to submit matter intake");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle opening conflict check form
  const handleOpenConflictCheck = async () => {
    // First, save draft submission if not already saved
    let submissionId = draftSubmissionId;
    
    if (!submissionId) {
      try {
        // Create a draft submission
        const draftData = { ...formData, status: "draft" };
        const response = await axios.post(`${API}/submissions`, { data: draftData });
        submissionId = response.data.id;
        setDraftSubmissionId(submissionId);
      } catch (error) {
        console.error("Failed to save draft:", error);
        toast.error("Failed to save draft. Please try again.");
        return;
      }
    }
    
    // Navigate to conflict check form with return URL
    navigate(`/conflict-check?submission_id=${submissionId}&return_to=/new-matter`);
  };

  // Link an existing conflict check by submission ID
  const handleLinkExistingConflict = async (submissionId) => {
    if (!submissionId) return;
    setDraftSubmissionId(submissionId);
    await fetchConflictStatus(submissionId);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1ClientInfo formData={formData} updateField={updateField} feeEarners={feeEarners} backupStaff={backupStaff} />;
      case 2:
        return <Step2MatterType formData={formData} updateField={updateField} />;
      case 3:
        return <Step3ConflictLink 
          conflictCheck={conflictCheck} 
          conflictLoading={conflictLoading}
          onLinkConflict={handleLinkExistingConflict}
          formData={formData}
        />;
      case 4:
        return <Step4MatterDetails formData={formData} updateField={updateField} isFieldVisible={isFieldVisible} />;
      case 5:
        return <Step5Pricing formData={formData} updateField={updateField} isFieldVisible={isFieldVisible} />;
      case 6:
        return <Step6Scope formData={formData} updateField={updateField} standardScope={standardScope} />;
      case 7:
        return <Step7Review 
          formData={formData} 
          standardScope={standardScope} 
          conflictCheck={conflictCheck}
          conflictLoading={conflictLoading}
          onOpenConflictCheck={handleOpenConflictCheck}
          canSubmit={canSubmit()}
          submissionId={draftSubmissionId}
          feeEarners={feeEarners}
        />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10" />
              <div>
                <h1 className="text-lg font-bold text-teal font-semibold">New FORF</h1>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-txt-muted hover:text-teal"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      {/* Conflict Check Gate — must link a CC before proceeding */}
      {!conflictCheck && !conflictLoading && !searchParams.get('conflict_id') && !searchParams.get('submission_id') && (
        <div className="max-w-2xl mx-auto px-4 pt-12 pb-8" data-testid="cc-gate-overlay">
          <div className="rounded-md border-2 border-amber-500/30 bg-navy p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-md bg-status-amber/10 border border-amber-500/30">
                <KeyRound className="w-8 h-8 text-status-amber" />
              </div>
            </div>
            <h2 className="text-lg font-bold text-txt mb-2">Conflict Check Required</h2>
            <p className="text-sm text-txt-muted mb-6 max-w-md mx-auto">
              The Golden Rule requires a completed Conflict Check before you can open a new FORF. Search for a passed, deferred, or N/A conflict check to unlock this form.
            </p>
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-txt-muted" />
                <input
                  type="text"
                  value={ccSearchQuery}
                  onChange={e => setCcSearchQuery(e.target.value)}
                  placeholder="Search by client name or matter reference..."
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/50"
                  data-testid="cc-gate-search"
                />
                {ccSearching && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 text-txt-muted animate-spin" />}
              </div>
              {ccSearchResults.length > 0 && (
                <div className="mt-2 bg-navy-card border border-navy-border rounded-md shadow-xl max-h-64 overflow-y-auto">
                  {ccSearchResults.map((cc, i) => {
                    const acceptable = isCcAcceptable(cc);
                    return (
                      <button
                        key={cc.id || i}
                        onClick={() => acceptable && linkConflictCheck(cc)}
                        disabled={!acceptable}
                        className={`w-full text-left px-4 py-3 border-b border-navy-border last:border-0 flex items-center justify-between ${acceptable ? "hover:bg-navy-hover cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
                        data-testid={`cc-gate-result-${i}`}
                      >
                        <div>
                          <span className="text-sm font-medium text-txt">{cc.client_name || cc.matter_reference || cc.provisional_id}</span>
                          <span className="text-xs text-txt-muted block">{cc.matter_reference || cc.provisional_id} · {cc.decision_maker_name || ""}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-md border ${
                          cc.conflict_status === "clear" ? "bg-teal-dim/30 border-teal/30 text-teal" :
                          cc.conflict_status === "clear_with_conditions" ? "bg-status-amber-dim/30 border-amber-500/30 text-status-amber" :
                          cc.conflict_status === "deferred" ? "bg-blue-900/30 border-blue-500/30 text-blue-400" :
                          cc.conflict_status === "na" || cc.conflict_status === "not_needed" ? "bg-slate-700/30 border-slate-500/30 text-slate-400" :
                          "bg-status-red-dim/30 border-red-500/30 text-status-red"
                        }`}>
                          {cc.conflict_status?.replace(/_/g, " ")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {ccSearchQuery.length >= 2 && ccSearchResults.length === 0 && !ccSearching && (
                <p className="text-xs text-txt-muted mt-2">No conflict checks found</p>
              )}
            </div>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => navigate("/conflicts-search")} className="border-navy-border text-txt-muted hover:text-white hover:bg-navy-hover" data-testid="cc-gate-new-search">
                <Shield className="w-4 h-4 mr-1.5" /> New Conflict Search
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} className="border-navy-border text-txt-muted hover:text-white hover:bg-navy-hover" data-testid="cc-gate-back">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Form — only visible once CC is linked */}
      {(conflictCheck || conflictLoading || searchParams.get('conflict_id') || searchParams.get('submission_id')) && (
      <>
      {/* Gating Banner */}
      {gating && !gating.ob_complete && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="p-4 bg-status-amber-dim/30 border-2 border-amber-500/40 rounded-md" data-testid="ob-gating-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-status-amber flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-status-amber">Client Onboarding Not Complete</p>
                <p className="text-sm text-status-amber mt-1">
                  The FORF cannot be submitted until the Client Onboarding (Stage 2) is complete.
                  Current OB status: <strong>{gating.ob_status || "not started"}</strong>
                </p>
                {matterRef && (
                  <a href={`/onboarding?ids=${matterRef}`} className="text-sm text-status-amber underline mt-1 inline-block">
                    View onboarding status
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stepper */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="relative flex justify-between mb-12">
          {/* Progress Line */}
          <div className="stepper-line">
            <div 
              className="stepper-line-progress" 
              style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
            />
          </div>
          
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isComplete = currentStep > step.id;
            const isLocked = !conflictCheck && step.id > 3;
            
            return (
              <div 
                key={step.id}
                className={`flex flex-col items-center z-10 px-2 ${isLocked ? 'opacity-40' : ''}`}
                title={isLocked ? 'Complete conflict check first' : step.title}
              >
                <div 
                  className={cn(
                    "w-10 h-10 rounded-md flex items-center justify-center border-2 transition-all",
                    isLocked ? "bg-navy-hover border-navy-border text-txt-faint" :
                    isComplete ? "bg-teal border-teal text-white" :
                    isActive ? "bg-navy-hover border-teal text-teal" :
                    "bg-navy-hover border-navy-border text-txt-muted"
                  )}
                >
                  {isLocked ? <Lock className="w-4 h-4" /> : isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={cn(
                  "mt-2 text-xs font-medium hidden sm:block",
                  isLocked ? "text-txt-faint" :
                  isActive || isComplete ? "text-teal" : "text-txt-muted"
                )}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Form Content */}
        <div className="glass-card p-8 rounded-md">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="border-navy-border hover:bg-navy-hover/60"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep < 7 ? (
            <Button
              onClick={handleNext}
              className="bg-teal hover:bg-teal-hover text-white"
              data-testid="next-step-btn"
            >
              Next Step
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !canSubmit()}
              className={cn(
                "text-white gap-2",
                canSubmit() 
                  ? "bg-teal hover:bg-teal-hover" 
                  : "bg-slate-400 cursor-not-allowed"
              )}
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Submit & Generate Letter
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </>
    )}
    </div>
  );
}

// Step 1: Client Information
function Step1ClientInfo({ formData, updateField, feeEarners = [], backupStaff = [] }) {
  return (
    <div className="space-y-6">
      {/* Submitter Section */}
      <div className="bg-navy-hover/60 border border-navy-border rounded-md p-4 mb-6">
        <h3 className="text-lg font-semibold text-txt mb-3 flex items-center gap-2">
          <Mail className="w-5 h-5 text-teal" />
          Your Details (Form Submitter)
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          The CCL document will be emailed to this address upon submission.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="submitter_name" className="text-txt-muted font-medium">
              Your Name <span className="text-status-red">*</span>
            </Label>
            <Input
              id="submitter_name"
              value={formData.submitter_name}
              onChange={(e) => updateField("submitter_name", e.target.value)}
              placeholder="Your name"
              className="border-navy-border focus:border-teal focus:ring-teal"
              data-testid="submitter-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="submitter_email" className="text-txt-muted font-medium">
              Your Email <span className="text-status-red">*</span>
            </Label>
            <Input
              id="submitter_email"
              type="email"
              value={formData.submitter_email}
              onChange={(e) => updateField("submitter_email", e.target.value)}
              placeholder="your.name@bonsai.law"
              className="border-navy-border focus:border-teal focus:ring-teal"
              data-testid="submitter-email-input"
            />
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-semibold text-teal font-semibold mb-2">
          Client & Matter Information
        </h2>
        <p className="text-slate-500">Enter the basic client and matter details.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="Q1" className="text-teal font-medium">
            Client Name(s) <span className="text-status-red">*</span>
          </Label>
          <Input
            id="Q1"
            value={formData.Q1}
            onChange={(e) => updateField("Q1", e.target.value)}
            placeholder="e.g., John Smith"
            className="border-navy-border focus:border-teal focus:ring-teal"
            data-testid="client-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="Q3" className="text-teal font-medium">
            Matter Title <span className="text-status-red">*</span>
          </Label>
          <Input
            id="Q3"
            value={formData.Q3}
            onChange={(e) => {
              if (e.target.value.length <= 40) {
                updateField("Q3", e.target.value);
              }
            }}
            placeholder="e.g., Sale of ABC Ltd"
            className="border-navy-border focus:border-teal focus:ring-teal"
            data-testid="matter-title-input"
            maxLength={40}
          />
          <div className="text-xs space-y-1">
            <p className={formData.Q3.length >= 35 ? "text-status-amber font-medium" : "text-slate-500"}>
              {formData.Q3.length}/40 characters
            </p>
            <p className="text-slate-400">
              <strong>Format:</strong> Sale of [Co] • Purchase of [Co] • Dispute with [Party] • Lease of [Property]
            </p>
            <p className="text-slate-400">
              <strong>Tips:</strong> Abbreviate names (max 15 chars), use short addresses (10 High St), no dates/file numbers
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="Q2" className="text-teal font-medium">
          Client Address <span className="text-status-red">*</span>
        </Label>
        <Textarea
          id="Q2"
          value={formData.Q2}
          onChange={(e) => updateField("Q2", e.target.value)}
          placeholder="Full postal address"
          rows={3}
          className="border-navy-border focus:border-teal focus:ring-teal"
          data-testid="client-address-input"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="Q4" className="text-teal font-medium">
            Our Reference
          </Label>
          <Input
            id="Q4"
            value={formData.Q4}
            onChange={(e) => updateField("Q4", e.target.value)}
            placeholder="Will be auto-generated in Step 2"
            className="border-navy-border focus:border-teal focus:ring-teal"
            data-testid="our-ref-input"
          />
          <p className="text-xs text-slate-400">Auto-generated from fee earner + practice area in Step 2, or enter manually</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="Q5" className="text-teal font-medium">
            Client/Counterparty Reference
          </Label>
          <Input
            id="Q5"
            value={formData.Q5}
            onChange={(e) => updateField("Q5", e.target.value)}
            placeholder="Optional"
            className="border-navy-border focus:border-teal focus:ring-teal"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="Q6" className="text-teal font-medium">
              Fee Earner <span className="text-status-red">*</span>
            </Label>
            <InfoTooltip id="fee_earner" size="sm" />
          </div>
          <Select value={formData.Q6} onValueChange={(v) => updateField("Q6", v)}>
            <SelectTrigger className="border-navy-border focus:border-teal focus:ring-teal" data-testid="fee-earner-select">
              <SelectValue placeholder="Select fee earner" />
            </SelectTrigger>
            <SelectContent>
              {feeEarners.map((fe) => (
                <SelectItem key={fe.name} value={fe.name}>
                  {fe.name} ({fe.position})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="Q7" className="text-teal font-medium">
            Backup Staff Member
          </Label>
          <Select value={formData.Q7} onValueChange={(v) => updateField("Q7", v)}>
            <SelectTrigger className="border-navy-border focus:border-teal focus:ring-teal">
              <SelectValue placeholder="Select backup" />
            </SelectTrigger>
            <SelectContent>
              {backupStaff.map((staff) => (
                <SelectItem key={staff.name} value={staff.name}>
                  {staff.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-teal font-medium">
            Is the client a private individual or a business? <span className="text-status-red">*</span>
          </Label>
          <InfoTooltip id="client_type" size="sm" />
        </div>
        <RadioGroup 
          value={formData.Q11} 
          onValueChange={(v) => updateField("Q11", v)}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Private individual" id="q11-private" />
            <Label htmlFor="q11-private" className="font-normal cursor-pointer">Private individual</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Business / organisation" id="q11-business" />
            <Label htmlFor="q11-business" className="font-normal cursor-pointer">Business / organisation</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Test Submission Flag */}
      <div className="p-4 bg-status-amber-dim/30 border border-amber-500/40 rounded-md">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="is_test_submission"
            checked={formData.is_test_submission}
            onCheckedChange={(checked) => updateField("is_test_submission", checked)}
            className="mt-0.5 border-amber-400 data-[state=checked]:bg-status-amber data-[state=checked]:border-amber-500"
            data-testid="test-submission-checkbox"
          />
          <div>
            <Label htmlFor="is_test_submission" className="font-medium text-status-amber cursor-pointer flex items-center gap-2">
              <FlaskConical className="w-4 h-4" />
              This is a test submission (for internal pilot only)
            </Label>
            <p className="text-xs text-status-amber/80 mt-1">
              Check this box for pilot testing. Test submissions will be clearly marked and can be filtered/deleted later.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2: Matter Type
function Step2MatterType({ formData, updateField }) {
  const [matterReferencePreview, setMatterReferencePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sharepointMatterTypes, setSharepointMatterTypes] = useState([]);

  // Fetch SharePoint matter types on mount
  useEffect(() => {
    const fetchMatterTypes = async () => {
      try {
        const response = await axios.get(`${API}/sharepoint/matter-types`);
        setSharepointMatterTypes(response.data);
      } catch (error) {
        console.error("Failed to fetch SharePoint matter types:", error);
      }
    };
    fetchMatterTypes();
  }, []);

  // Fetch matter reference preview when fee earner and SharePoint matter type change
  useEffect(() => {
    const fetchPreview = async () => {
      if (!formData.Q6 || !formData.sharepoint_matter_type) {
        setMatterReferencePreview(null);
        return;
      }
      
      setLoadingPreview(true);
      try {
        const response = await axios.get(`${API}/sharepoint/preview-reference`, {
          params: {
            fee_earner: formData.Q6,
            matter_type: formData.sharepoint_matter_type
          }
        });
        setMatterReferencePreview(response.data);
        // Auto-populate Q4 with the generated reference
        updateField("Q4", response.data.matter_reference);
      } catch (error) {
        console.error("Failed to fetch matter reference preview:", error);
        setMatterReferencePreview(null);
      } finally {
        setLoadingPreview(false);
      }
    };
    
    fetchPreview();
  }, [formData.Q6, formData.sharepoint_matter_type]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal font-semibold mb-2">
          Matter Type
        </h2>
        <p className="text-slate-500">Select the type of matter to determine the correct CCL template and SharePoint folder.</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="Q9" className="text-teal font-medium">
            CCL / Matter Type <span className="text-status-red">*</span>
          </Label>
          <InfoTooltip id="matter_type" size="sm" />
        </div>
        <Select value={formData.Q9} onValueChange={(v) => updateField("Q9", v)}>
          <SelectTrigger className="border-navy-border focus:border-teal focus:ring-teal h-12" data-testid="matter-type-select">
            <SelectValue placeholder="Select matter type" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {MATTER_TYPES.map((mt) => (
              <SelectItem key={mt.value} value={mt.value}>
                {mt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-400">This controls which CCL template and standard scope wording are used.</p>
      </div>

      {/* SharePoint Practice Area Category */}
      <div className="space-y-2">
        <Label htmlFor="sharepoint_matter_type" className="text-teal font-medium">
          Practice Area (for SharePoint) <span className="text-status-red">*</span>
        </Label>
        <Select 
          value={formData.sharepoint_matter_type || ""} 
          onValueChange={(v) => updateField("sharepoint_matter_type", v)}
        >
          <SelectTrigger className="border-navy-border focus:border-teal focus:ring-teal h-12" data-testid="sharepoint-matter-type-select">
            <SelectValue placeholder="Select practice area for SharePoint filing" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {sharepointMatterTypes.length > 0 ? (
              sharepointMatterTypes.map((mt) => (
                <SelectItem key={mt.value} value={mt.value}>
                  {mt.label} ({mt.practice_code})
                </SelectItem>
              ))
            ) : (
              <>
                <SelectItem value="resi">Residential (001)</SelectItem>
                <SelectItem value="coco">Corporate/Commercial (002)</SelectItem>
                <SelectItem value="comm_prop">Commercial Property (003)</SelectItem>
                <SelectItem value="disputes">Disputes/Litigation (006)</SelectItem>
                <SelectItem value="ip">Intellectual Property (007)</SelectItem>
                <SelectItem value="private_client">Private Client (008)</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-400">Determines the SharePoint folder location and matter reference format.</p>
      </div>

      {/* Auto-generated Matter Reference Preview */}
      {(formData.Q6 && formData.sharepoint_matter_type) && (
        <div className="p-4 bg-gradient-to-r from-teal-dim to-teal-dim border border-teal rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-teal" />
            <Label className="text-teal font-medium text-sm">Auto-Generated Matter Reference</Label>
          </div>
          {loadingPreview ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Generating reference...</span>
            </div>
          ) : matterReferencePreview ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-mono font-bold text-teal" data-testid="matter-reference-preview">
                  {matterReferencePreview.matter_reference}
                </span>
                <span className="text-xs bg-teal-dim text-teal px-2 py-1 rounded">
                  {matterReferencePreview.format}
                </span>
              </div>
              <div className="text-xs text-txt-muted flex flex-wrap gap-x-4 gap-y-1">
                <span>📁 Folder: <span className="font-medium">/SL/{matterReferencePreview.parent_folder}/</span></span>
                <span>👤 Initials: <span className="font-medium">{matterReferencePreview.fee_earner_initials}</span></span>
                <span>📋 Code: <span className="font-medium">{matterReferencePreview.practice_area_code}</span></span>
                <span>#️⃣ Seq: <span className="font-medium">{matterReferencePreview.sequential_number}</span></span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Select fee earner and practice area to generate reference</p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="Q10" className="text-teal font-medium">
          Internal Matter Description
        </Label>
        <Textarea
          id="Q10"
          value={formData.Q10}
          onChange={(e) => updateField("Q10", e.target.value)}
          placeholder="Optional internal notes about this matter"
          rows={3}
          className="border-navy-border focus:border-teal focus:ring-teal"
        />
      </div>

      <div className="space-y-3">
        <Label className="text-teal font-medium">
          Consumer 14-day cooling-off applies?
        </Label>
        <RadioGroup 
          value={formData.Q12} 
          onValueChange={(v) => updateField("Q12", v)}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Yes" id="q12-yes" />
            <Label htmlFor="q12-yes" className="font-normal cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="No" id="q12-no" />
            <Label htmlFor="q12-no" className="font-normal cursor-pointer">No</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-3">
        <Label className="text-teal font-medium">
          Are we acting for more than one client on this matter?
        </Label>
        <RadioGroup 
          value={formData.Q30} 
          onValueChange={(v) => updateField("Q30", v)}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Yes" id="q30-yes" />
            <Label htmlFor="q30-yes" className="font-normal cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="No" id="q30-no" />
            <Label htmlFor="q30-no" className="font-normal cursor-pointer">No</Label>
          </div>
        </RadioGroup>
      </div>

      {formData.Q30 === "Yes" && (
        <div className="space-y-2">
          <Label htmlFor="Q30_details" className="text-teal font-medium">
            Details of multiple clients / relationships
          </Label>
          <Textarea
            id="Q30_details"
            value={formData.Q30_details}
            onChange={(e) => updateField("Q30_details", e.target.value)}
            placeholder="Describe the relationships between clients"
            rows={3}
            className="border-navy-border focus:border-teal focus:ring-teal"
          />
        </div>
      )}
    </div>
  );
}

// Step 3: Link Existing Conflict Check (read-only)
function Step3ConflictLink({ conflictCheck, conflictLoading, onLinkConflict, formData }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async (query) => {
    const q = query !== undefined ? query : searchQuery;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/conflict-checks/search`, {
        params: { q: q.trim() }
      });
      setSearchResults(res.data.results || []);
      if ((res.data.results || []).length === 0 && q.trim()) {
        toast.info("No conflict checks found. Run a conflict check first from the Conflicts page.");
      }
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleShowRecent = () => {
    setSearchQuery("");
    handleSearch("");
  };

  const getStatusBadge = (status) => {
    if (status === "clear") return { label: "Cleared", color: "bg-status-green-dim text-status-green border-green-300", icon: CheckCircle2 };
    if (status === "clear_with_conditions") return { label: "Cleared (Conditions)", color: "bg-status-amber-dim text-status-amber border-amber-300", icon: AlertTriangle };
    if (status === "conflict_found") return { label: "Conflict Found", color: "bg-status-red-dim text-status-red border-red-300", icon: XCircle };
    if (status === "do_not_act") return { label: "Do Not Act", color: "bg-status-red-dim text-status-red border-red-300", icon: XCircle };
    return { label: status || "Pending", color: "bg-slate-100 text-txt-muted border-slate-300", icon: Clock };
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">
          Conflict Check
        </h2>
        <p className="text-slate-500">
          Link this FORF to the existing conflict check completed in Stage 1.
        </p>
      </div>

      {conflictLoading ? (
        <div className="flex items-center gap-3 p-6 bg-navy-hover/60 rounded-md">
          <Loader2 className="w-5 h-5 animate-spin text-teal" />
          <span className="text-txt-muted">Loading conflict check status...</span>
        </div>
      ) : conflictCheck ? (
        /* Conflict check is linked - show read-only status */
        <div className="p-6 bg-white border-2 border-green-200 rounded-md space-y-4" data-testid="linked-conflict-check">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-teal" />
            <h3 className="font-semibold text-teal">Conflict Check Linked</h3>
            {(() => {
              const badge = getStatusBadge(conflictCheck.conflict_status);
              const Icon = badge.icon;
              return (
                <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm rounded-md border ${badge.color}`}>
                  <Icon className="w-4 h-4" /> {badge.label}
                </span>
              );
            })()}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Client:</span>
              <span className="ml-2 font-medium">{conflictCheck.client_name || formData.Q1}</span>
            </div>
            <div>
              <span className="text-slate-500">Decision by:</span>
              <span className="ml-2 font-medium">{conflictCheck.decision_maker_name || "—"}</span>
            </div>
            {conflictCheck.other_side_parties?.length > 0 && (
              <div className="col-span-2">
                <span className="text-slate-500">Other parties:</span>
                <div className="mt-1 space-y-1">
                  {conflictCheck.other_side_parties.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-slate-100 text-txt-muted mr-2">
                      {p.name || p.legal_name} ({p.role || p.party_type || "party"})
                    </span>
                  ))}
                </div>
              </div>
            )}
            {conflictCheck.decision_notes && (
              <div className="col-span-2">
                <span className="text-slate-500">Notes:</span>
                <p className="mt-1 text-txt">{conflictCheck.decision_notes}</p>
              </div>
            )}
          </div>
          {conflictCheck.conflict_status !== "clear" && conflictCheck.conflict_status !== "clear_with_conditions" && !conflictCheck.has_override && (
            <div className="p-3 bg-status-red-dim border border-red-200 rounded text-sm text-status-red">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              This conflict check has not been cleared. The FORF cannot be submitted until the conflict is resolved or overridden.
            </div>
          )}
        </div>
      ) : (
        /* No conflict check linked - show search/link UI */
        <div className="space-y-4">
          <div className="p-6 bg-status-amber-dim border-2 border-amber-200 rounded-md" data-testid="no-conflict-linked">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-6 h-6 text-status-amber" />
              <h3 className="font-semibold text-status-amber">No Conflict Check Linked</h3>
            </div>
            <p className="text-sm text-status-amber mb-4">
              A conflict check must be completed before the FORF can proceed. Search for an existing conflict check below, or run one from the <a href="/conflicts" className="underline font-medium">Conflicts page</a>.
            </p>
            
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by client name, reference, or submission ID..."
                className="border-amber-300 flex-1"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                data-testid="conflict-search-input"
              />
              <Button
                onClick={() => handleSearch()}
                disabled={searching || !searchQuery.trim()}
                className="bg-teal hover:bg-teal-hover text-white"
                data-testid="conflict-search-btn"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
              </Button>
            </div>
            <div className="mt-2">
              <button
                onClick={handleShowRecent}
                disabled={searching}
                className="text-sm text-teal hover:underline font-medium"
                data-testid="conflict-show-recent-btn"
              >
                {searching ? "Loading..." : "Show recent conflict checks"}
              </button>
            </div>
          </div>

          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-txt-muted">
                {searchQuery.trim() ? "Found conflict checks:" : "Recent conflict checks:"}
              </h4>
              {searchResults.map((result, idx) => {
                const badge = getStatusBadge(result.conflict_status);
                const Icon = badge.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-white border border-navy-border rounded-md hover:border-teal/30 transition-colors cursor-pointer"
                    onClick={() => onLinkConflict(result.submission_id)}
                    data-testid={`conflict-result-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Shield className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-white">{result.client_name || "Unknown"}</span>
                          {(result.matter_reference || result.provisional_id) && (
                            <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{result.matter_reference || result.provisional_id}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {result.created_at && new Date(result.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          {result.decision_maker_name && <span className="ml-2">by {result.decision_maker_name}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border ${badge.color}`}>
                        <Icon className="w-3 h-3" /> {badge.label}
                      </span>
                      <Button size="sm" variant="outline" className="border-teal text-teal">
                        Link
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Step 4: Matter Details (Conditional fields)
function Step4MatterDetails({ formData, updateField, isFieldVisible }) {
  const visibleFields = [];
  
  if (isFieldVisible("Q8_property_address")) {
    visibleFields.push({ id: "Q8_property_address", label: "Property Address", placeholder: "Full property address" });
  }
  if (isFieldVisible("Q8_seller_name")) {
    visibleFields.push({ id: "Q8_seller_name", label: "Seller Name", placeholder: "Name of seller" });
  }
  if (isFieldVisible("Q8_buyer_name")) {
    visibleFields.push({ id: "Q8_buyer_name", label: "Buyer Name", placeholder: "Name of buyer" });
  }
  if (isFieldVisible("Q8_tenant_name")) {
    visibleFields.push({ id: "Q8_tenant_name", label: "Tenant Name", placeholder: "Name of tenant" });
  }
  if (isFieldVisible("Q8_landlord_name")) {
    visibleFields.push({ id: "Q8_landlord_name", label: "Landlord Name", placeholder: "Name of landlord" });
  }
  if (isFieldVisible("Q8_target_company")) {
    visibleFields.push({ id: "Q8_target_company", label: "Target Company Name", placeholder: "Name of target company" });
  }
  if (isFieldVisible("Q8_seller_business_name")) {
    visibleFields.push({ id: "Q8_seller_business_name", label: "Seller Business Name", placeholder: "Name of seller's business" });
  }
  if (isFieldVisible("Q8_new_lender")) {
    visibleFields.push({ id: "Q8_new_lender", label: "New Lender Name", placeholder: "Name of new lender" });
  }
  if (isFieldVisible("Q8_existing_lender")) {
    visibleFields.push({ id: "Q8_existing_lender", label: "Existing Lender Name", placeholder: "Name of existing lender" });
  }
  if (isFieldVisible("Q8_new_owner")) {
    visibleFields.push({ id: "Q8_new_owner", label: "New Owner / Co-owner Name", placeholder: "Name of new owner" });
  }
  if (isFieldVisible("Q8_auction_house")) {
    visibleFields.push({ id: "Q8_auction_house", label: "Auction House Name", placeholder: "Name of auction house" });
  }
  if (isFieldVisible("Q8_company_name_shareholders")) {
    visibleFields.push({ id: "Q8_company_name_shareholders", label: "Company Name (Shareholders Agreement)", placeholder: "Name of company" });
  }
  if (isFieldVisible("Q8_current_owner_name")) {
    visibleFields.push({ id: "Q8_current_owner_name", label: "Current Owner Name (MBI/MBO)", placeholder: "Name of current owner" });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal font-semibold mb-2">
          Matter-Specific Details
        </h2>
        <p className="text-slate-500">
          {visibleFields.length > 0 
            ? "Enter the details specific to this matter type."
            : "No additional details required for this matter type."
          }
        </p>
      </div>

      {visibleFields.length === 0 ? (
        <div className="p-8 bg-navy-hover/60 rounded-md text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            No matter-specific fields are required for the selected matter type.
            <br />You can proceed to the next step.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visibleFields.map((field) => (
            <div key={field.id} className="space-y-2">
              <Label htmlFor={field.id} className="text-teal font-medium">
                {field.label}
              </Label>
              <Input
                id={field.id}
                value={formData[field.id] || ""}
                onChange={(e) => updateField(field.id, e.target.value)}
                placeholder={field.placeholder}
                className="border-navy-border focus:border-teal focus:ring-teal"
                data-testid={`${field.id}-input`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Residential Conveyancing Fields */}
      {(isFieldVisible("tenure") || isFieldVisible("completion_date")) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-navy-border">
          {isFieldVisible("tenure") && (
            <div className="space-y-2">
              <Label htmlFor="tenure" className="text-teal font-medium">
                Tenure <span className="text-slate-400 text-sm font-normal">(optional)</span>
              </Label>
              <Select
                value={formData.tenure || ""}
                onValueChange={(value) => updateField("tenure", value)}
              >
                <SelectTrigger id="tenure" data-testid="tenure-select">
                  <SelectValue placeholder="Select tenure type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="freehold">Freehold</SelectItem>
                  <SelectItem value="leasehold">Leasehold</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Used for completion statements. Can be updated later.</p>
            </div>
          )}
          {isFieldVisible("completion_date") && (
            <div className="space-y-2">
              <Label htmlFor="completion_date" className="text-teal font-medium">
                Expected Completion Date <span className="text-slate-400 text-sm font-normal">(optional)</span>
              </Label>
              <Input
                id="completion_date"
                type="date"
                value={formData.completion_date || ""}
                onChange={(e) => updateField("completion_date", e.target.value)}
                className="border-navy-border focus:border-teal focus:ring-teal"
                data-testid="completion-date-input"
              />
              <p className="text-xs text-slate-500">Can be updated on the completion statement.</p>
            </div>
          )}
        </div>
      )}

      {/* SPA-specific fields */}
      {(isFieldVisible("Q24_number_of_shares") || isFieldVisible("Q25_percentage_shareholding")) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-navy-border">
          {isFieldVisible("Q24_number_of_shares") && (
            <div className="space-y-2">
              <Label htmlFor="Q24_number_of_shares" className="text-teal font-medium">
                Number of Shares
              </Label>
              <Input
                id="Q24_number_of_shares"
                value={formData.Q24_number_of_shares || ""}
                onChange={(e) => updateField("Q24_number_of_shares", e.target.value)}
                placeholder="e.g., 1,000"
                className="border-navy-border focus:border-teal focus:ring-teal"
              />
            </div>
          )}
          {isFieldVisible("Q25_percentage_shareholding") && (
            <div className="space-y-2">
              <Label htmlFor="Q25_percentage_shareholding" className="text-teal font-medium">
                Percentage of Shareholding
              </Label>
              <Input
                id="Q25_percentage_shareholding"
                value={formData.Q25_percentage_shareholding || ""}
                onChange={(e) => updateField("Q25_percentage_shareholding", e.target.value)}
                placeholder="e.g., 50%"
                className="border-navy-border focus:border-teal focus:ring-teal"
              />
            </div>
          )}
        </div>
      )}

      {/* Contracts-specific fields */}
      {isFieldVisible("Q27_business_name_contracts") && (
        <div className="pt-4 border-t border-navy-border space-y-6">
          <div className="space-y-2">
            <Label htmlFor="Q27_business_name_contracts" className="text-teal font-medium">
              Business Name
            </Label>
            <Input
              id="Q27_business_name_contracts"
              value={formData.Q27_business_name_contracts || ""}
              onChange={(e) => updateField("Q27_business_name_contracts", e.target.value)}
              placeholder="Name of the business"
              className="border-navy-border focus:border-teal focus:ring-teal"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Q27_contract_scope" className="text-teal font-medium">
              Headline Contract Scope
            </Label>
            <Input
              id="Q27_contract_scope"
              value={formData.Q27_contract_scope || ""}
              onChange={(e) => updateField("Q27_contract_scope", e.target.value)}
              placeholder="Brief description of the contract work"
              className="border-navy-border focus:border-teal focus:ring-teal"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["Q27_scope1", "Q27_scope2", "Q27_scope3", "Q27_scope4"].map((field, idx) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={field} className="text-teal font-medium">
                  Specific Scope {idx + 1}
                </Label>
                <Input
                  id={field}
                  value={formData[field] || ""}
                  onChange={(e) => updateField(field, e.target.value)}
                  placeholder={`Scope item ${idx + 1}`}
                  className="border-navy-border focus:border-teal focus:ring-teal"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other / General CCL specific fields */}
      {isFieldVisible("Q28_general_description") && (
        <div className="pt-4 border-t border-navy-border space-y-6">
          <div className="bg-teal-dim rounded-md p-4 border border-teal">
            <h3 className="text-sm font-semibold text-teal mb-1">Other / General CCL</h3>
            <p className="text-xs text-slate-500">These fields are specific to the General CCL template and will be merged into the client care letter.</p>
          </div>

          {/* Staff details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="Q30_fee_earner_position" className="text-teal font-medium">
                Fee Earner Position <span className="text-status-red">*</span>
              </Label>
              <Input
                id="Q30_fee_earner_position"
                value={formData.Q30_fee_earner_position || ""}
                onChange={(e) => updateField("Q30_fee_earner_position", e.target.value)}
                placeholder="e.g. Director, Senior Associate"
                className="border-navy-border focus:border-teal focus:ring-teal"
                data-testid="Q30-fee-earner-position-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="Q32_backup_staff_position" className="text-teal font-medium">
                Backup Staff Position <span className="text-status-red">*</span>
              </Label>
              <Input
                id="Q32_backup_staff_position"
                value={formData.Q32_backup_staff_position || ""}
                onChange={(e) => updateField("Q32_backup_staff_position", e.target.value)}
                placeholder="e.g. Paralegal, Legal Assistant"
                className="border-navy-border focus:border-teal focus:ring-teal"
                data-testid="Q32-backup-staff-position-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="Q31_backup_staff_email" className="text-teal font-medium">
              Backup Staff Email <span className="text-status-red">*</span>
            </Label>
            <Input
              id="Q31_backup_staff_email"
              type="email"
              value={formData.Q31_backup_staff_email || ""}
              onChange={(e) => updateField("Q31_backup_staff_email", e.target.value)}
              placeholder="backup.staff@bonsailaw.co.uk"
              className="border-navy-border focus:border-teal focus:ring-teal"
              data-testid="Q31-backup-staff-email-input"
            />
          </div>

          {/* Work description */}
          <div className="space-y-2">
            <Label htmlFor="Q28_general_description" className="text-teal font-medium">
              General Description of Work <span className="text-status-red">*</span>
            </Label>
            <Textarea
              id="Q28_general_description"
              value={formData.Q28_general_description || ""}
              onChange={(e) => updateField("Q28_general_description", e.target.value)}
              placeholder="Describe the scope and nature of the legal work being undertaken..."
              rows={4}
              className="border-navy-border focus:border-teal focus:ring-teal"
              data-testid="Q28-general-description-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="Q29_legal_work" className="text-teal font-medium">
              Legal Work Label <span className="text-status-red">*</span>
              <span className="text-slate-400 text-sm font-normal ml-2">
                (used in "fixed fee for [Legal Work]" sentence)
              </span>
            </Label>
            <Input
              id="Q29_legal_work"
              value={formData.Q29_legal_work || formData.Q3 || ""}
              onChange={(e) => updateField("Q29_legal_work", e.target.value)}
              placeholder="e.g. the sale of your property"
              className="border-navy-border focus:border-teal focus:ring-teal"
              data-testid="Q29-legal-work-input"
            />
          </div>
        </div>
      )}
    </div>
  );
}
function Step5Pricing({ formData, updateField, isFieldVisible }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal font-semibold mb-2">
          Pricing & Timescales
        </h2>
        <p className="text-slate-500">Set up the fee structure and timescales for this matter.</p>
      </div>

      <div className="space-y-3">
        <Label className="text-teal font-medium">
          Pricing Model <span className="text-status-red">*</span>
        </Label>
        <RadioGroup 
          value={formData.Q13} 
          onValueChange={(v) => updateField("Q13", v)}
          className="flex flex-wrap gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Fixed fee" id="q13-fixed" />
            <Label htmlFor="q13-fixed" className="font-normal cursor-pointer">Fixed fee</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Hourly" id="q13-hourly" />
            <Label htmlFor="q13-hourly" className="font-normal cursor-pointer">Hourly</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Blended / other" id="q13-blended" />
            <Label htmlFor="q13-blended" className="font-normal cursor-pointer">Blended / other</Label>
          </div>
        </RadioGroup>
      </div>

      {formData.Q13 === "Fixed fee" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="Q14" className="text-teal font-medium">
              Fixed Fee Amount (excluding VAT)
            </Label>
            <div className="relative">
              <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="Q14"
                type="text"
                value={formData.Q14}
                onChange={(e) => updateField("Q14", e.target.value)}
                placeholder="e.g., 1,500"
                className="pl-10 border-navy-border focus:border-teal focus:ring-teal"
                data-testid="fee-quote-input"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-teal font-medium">
              Fee Split
            </Label>
            <RadioGroup 
              value={formData.Q15} 
              onValueChange={(v) => updateField("Q15", v)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="50% on outset, 50% on exchange/completion (default)" id="q15-default" />
                <Label htmlFor="q15-default" className="font-normal cursor-pointer">
                  50% on outset, 50% on exchange/completion (default)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Other (specify below)" id="q15-other" />
                <Label htmlFor="q15-other" className="font-normal cursor-pointer">
                  Other (specify below)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {formData.Q15 === "Other (specify below)" && (
            <div className="space-y-2">
              <Label htmlFor="Q15_other" className="text-teal font-medium">
                Custom Fee Split
              </Label>
              <Input
                id="Q15_other"
                value={formData.Q15_other}
                onChange={(e) => updateField("Q15_other", e.target.value)}
                placeholder="Describe the fee split arrangement"
                className="border-navy-border focus:border-teal focus:ring-teal"
              />
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="Q16" className="text-teal font-medium">
            Estimated Disbursements
          </Label>
          <Input
            id="Q16"
            value={formData.Q16}
            onChange={(e) => updateField("Q16", e.target.value)}
            placeholder="e.g., £150-£300"
            className="border-navy-border focus:border-teal focus:ring-teal"
          />
        </div>

        <div className="space-y-3">
          <Label className="text-teal font-medium">
            Payment on Account Required?
          </Label>
          <RadioGroup 
            value={formData.Q17} 
            onValueChange={(v) => updateField("Q17", v)}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Yes" id="q17-yes" />
              <Label htmlFor="q17-yes" className="font-normal cursor-pointer">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="No" id="q17-no" />
              <Label htmlFor="q17-no" className="font-normal cursor-pointer">No</Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      {formData.Q17 === "Yes" && (
        <div className="space-y-2">
          <Label htmlFor="Q17_amount" className="text-teal font-medium">
            Payment on account amount (legal fees, before VAT)
          </Label>
          <div className="relative">
            <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="Q17_amount"
              value={formData.Q17_amount}
              onChange={(e) => updateField("Q17_amount", e.target.value)}
              placeholder="e.g., 250"
              className="pl-10 border-navy-border focus:border-teal focus:ring-teal"
            />
          </div>
        </div>
      )}

      {/* Timescale fields based on matter type */}
      <div className="pt-4 border-t border-navy-border">
        <h3 className="text-lg font-medium text-teal mb-4">Timescales</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isFieldVisible("Q18_timescale_searches") && (
            <div className="space-y-2">
              <Label htmlFor="Q18_timescale_searches" className="text-teal font-medium">
                Searches / Initial Phase (working days)
              </Label>
              <Input
                id="Q18_timescale_searches"
                value={formData.Q18_timescale_searches || ""}
                onChange={(e) => updateField("Q18_timescale_searches", e.target.value)}
                placeholder="e.g., 10-15"
                className="border-navy-border focus:border-teal focus:ring-teal"
              />
            </div>
          )}

          {isFieldVisible("Q18_estimated_timescale") && (
            <div className="space-y-2">
              <Label htmlFor="Q18_estimated_timescale" className="text-teal font-medium">
                Overall Timescale (weeks from exchange)
              </Label>
              <Input
                id="Q18_estimated_timescale"
                value={formData.Q18_estimated_timescale || ""}
                onChange={(e) => updateField("Q18_estimated_timescale", e.target.value)}
                placeholder="e.g., 4-6 weeks"
                className="border-navy-border focus:border-teal focus:ring-teal"
              />
            </div>
          )}

          {isFieldVisible("Q18_timescale_combined") && (
            <div className="space-y-2">
              <Label htmlFor="Q18_timescale_combined" className="text-teal font-medium">
                Combined Timescale (working days)
              </Label>
              <Input
                id="Q18_timescale_combined"
                value={formData.Q18_timescale_combined || ""}
                onChange={(e) => updateField("Q18_timescale_combined", e.target.value)}
                placeholder="e.g., 20-30"
                className="border-navy-border focus:border-teal focus:ring-teal"
              />
            </div>
          )}

          {isFieldVisible("Q18_timescale_review") && (
            <div className="space-y-2">
              <Label htmlFor="Q18_timescale_review" className="text-teal font-medium">
                Review Timescale (working days)
              </Label>
              <Input
                id="Q18_timescale_review"
                value={formData.Q18_timescale_review || ""}
                onChange={(e) => updateField("Q18_timescale_review", e.target.value)}
                placeholder="e.g., 3-5"
                className="border-navy-border focus:border-teal focus:ring-teal"
              />
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="Q19" className="text-teal font-medium">
          Client-imposed Cost Cap
        </Label>
        <Input
          id="Q19"
          value={formData.Q19}
          onChange={(e) => updateField("Q19", e.target.value)}
          placeholder="Optional - any cost limit agreed with client"
          className="border-navy-border focus:border-teal focus:ring-teal"
        />
      </div>

      <div className="space-y-3">
        <Label className="text-teal font-medium">
          Include back-up hourly rates wording?
        </Label>
        <RadioGroup 
          value={formData.Q21} 
          onValueChange={(v) => updateField("Q21", v)}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Yes" id="q21-yes" />
            <Label htmlFor="q21-yes" className="font-normal cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="No" id="q21-no" />
            <Label htmlFor="q21-no" className="font-normal cursor-pointer">No</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

// Step 6: Scope
function Step6Scope({ formData, updateField, standardScope }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal font-semibold mb-2">
          Scope of Services
        </h2>
        <p className="text-slate-500">Define what work will be included in this engagement.</p>
      </div>

      <div className="space-y-3">
        <Label className="text-teal font-medium">
          Scope of Services <span className="text-status-red">*</span>
        </Label>
        <RadioGroup 
          value={formData.Q32A} 
          onValueChange={(v) => updateField("Q32A", v)}
          className="space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Yes – use standard wording" id="q32a-standard" />
            <Label htmlFor="q32a-standard" className="font-normal cursor-pointer">
              Yes – use standard wording for this CCL type
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="No – I will write bespoke scope" id="q32a-bespoke" />
            <Label htmlFor="q32a-bespoke" className="font-normal cursor-pointer">
              No – I will write bespoke scope
            </Label>
          </div>
        </RadioGroup>
      </div>

      {formData.Q32A === "Yes – use standard wording" && (
        <>
          <div className="space-y-2">
            <Label className="text-teal font-medium">
              Standard Scope Preview
            </Label>
            <div className="p-4 bg-navy-hover/60 rounded-md border border-navy-border max-h-64 overflow-y-auto">
              <pre className="text-sm text-txt-muted whitespace-pre-wrap font-sans">
                {standardScope || "Select a matter type to see the standard scope."}
              </pre>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="Q32C" className="text-teal font-medium">
              Additional Scope (optional)
            </Label>
            <Textarea
              id="Q32C"
              value={formData.Q32C}
              onChange={(e) => updateField("Q32C", e.target.value)}
              placeholder="Add any additional scope items not covered by the standard wording"
              rows={4}
              className="border-navy-border focus:border-teal focus:ring-teal"
            />
            <p className="text-xs text-slate-400">
              This will be appended to the standard scope in the CCL.
            </p>
          </div>
        </>
      )}

      {formData.Q32A === "No – I will write bespoke scope" && (
        <div className="space-y-2">
          <Label htmlFor="Q32D" className="text-teal font-medium">
            Bespoke Scope – 'What we will do'
          </Label>
          <Textarea
            id="Q32D"
            value={formData.Q32D}
            onChange={(e) => updateField("Q32D", e.target.value)}
            placeholder="Enter the full scope of services for this matter"
            rows={8}
            className="border-navy-border focus:border-teal focus:ring-teal"
            data-testid="bespoke-scope-input"
          />
          <p className="text-xs text-slate-400">
            This will replace the standard scope entirely.
          </p>
        </div>
      )}
    </div>
  );
}

// Step 7: Review
function Step7Review({ formData, standardScope, conflictCheck, conflictLoading, onOpenConflictCheck, canSubmit, submissionId, feeEarners = [] }) {
  const matterType = MATTER_TYPES.find(m => m.value === formData.Q9);
  const feeEarner = feeEarners.find(f => f.name === formData.Q6);
  
  const scopeText = formData.Q32A === "No – I will write bespoke scope" 
    ? formData.Q32D 
    : (formData.Q32C ? `${standardScope}\n\n${formData.Q32C}` : standardScope);

  const getConflictStatusConfig = (status) => {
    switch (status) {
      case "clear":
        return { 
          label: "Clear", 
          color: "bg-status-green-dim text-status-green border-green-300",
          icon: CheckCircle2,
          iconColor: "text-status-green"
        };
      case "clear_with_conditions":
        return { 
          label: "Clear with conditions", 
          color: "bg-status-amber-dim text-status-amber border-amber-300",
          icon: AlertTriangle,
          iconColor: "text-status-amber"
        };
      case "do_not_act":
        return { 
          label: "Do not act", 
          color: "bg-status-red-dim text-status-red border-red-300",
          icon: XCircle,
          iconColor: "text-status-red"
        };
      default:
        return { 
          label: "Unknown", 
          color: "bg-slate-100 text-white",
          icon: Shield,
          iconColor: "text-txt-muted"
        };
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">
          Review & Submit
        </h2>
        <p className="text-slate-500">Review the details before generating the Client Care Letter.</p>
      </div>

      {/* Conflict Check Status Panel - CRITICAL */}
      <div className={`rounded-md border-2 overflow-hidden ${
        conflictCheck 
          ? getConflictStatusConfig(conflictCheck.conflict_status).color
          : 'bg-status-red-dim border-red-300'
      }`}>
        <div className="p-4">
          <h3 className="font-bold flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5" />
            Conflict Check Status
          </h3>
          
          {conflictLoading ? (
            <div className="flex items-center gap-2 text-txt-muted">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading conflict check status...
            </div>
          ) : conflictCheck ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {(() => {
                  const config = getConflictStatusConfig(conflictCheck.conflict_status);
                  const StatusIcon = config.icon;
                  return (
                    <>
                      <StatusIcon className={`w-6 h-6 ${config.iconColor}`} />
                      <div>
                        <span className="font-semibold">{config.label}</span>
                        <p className="text-sm opacity-80">
                          Decided by {conflictCheck.decision_maker_name} on {new Date(conflictCheck.decision_date).toLocaleDateString()}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
              
              {conflictCheck.decision_notes && (
                <div className="p-3 bg-white/50 rounded text-sm">
                  <strong>Notes/Conditions:</strong> {conflictCheck.decision_notes}
                </div>
              )}
              
              {conflictCheck.has_override && (
                <div className="p-3 bg-status-amber-dim rounded text-sm text-status-amber">
                  <strong>Override Applied:</strong> by {conflictCheck.override_by_name} - {conflictCheck.override_reason}
                </div>
              )}
              
              <Button
                onClick={onOpenConflictCheck}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View / Update Conflict Check
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-status-red">
                <XCircle className="w-5 h-5" />
                <span className="font-medium">No conflict check completed</span>
              </div>
              <p className="text-sm text-status-red">
                A conflict check must be completed before this FORF can be submitted.
              </p>
              <Button
                onClick={onOpenConflictCheck}
                className="bg-teal hover:bg-teal-hover text-white"
                data-testid="open-conflict-check-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Submit for Conflict Check
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Submission blocker warning */}
      {!canSubmit && (
        <div className="p-4 bg-status-red-dim border border-red-200 rounded-md">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-status-red flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-status-red">Cannot submit FORF</p>
              <p className="text-sm text-status-red mt-1">
                {!conflictCheck 
                  ? "A conflict check must be completed first."
                  : conflictCheck.conflict_status === "do_not_act" && !conflictCheck.has_override
                    ? "Conflict status is 'Do not act'. An admin/COLP override is required to proceed."
                    : "Conflict check must show 'Clear' or 'Clear with conditions' status."
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Summary Panel */}
      {submissionId && (
        <AIMatterSummary submissionId={submissionId} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Client Info */}
        <div className="p-4 bg-navy-hover/60 rounded-md space-y-3">
          <h3 className="font-medium text-teal flex items-center gap-2">
            <User className="w-4 h-4" />
            Client Information
          </h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Name:</span> {formData.Q1}</div>
            <div><span className="text-slate-500">Address:</span> {formData.Q2}</div>
            <div><span className="text-slate-500">Type:</span> {formData.Q11}</div>
          </div>
        </div>

        {/* Matter Info */}
        <div className="p-4 bg-navy-hover/60 rounded-md space-y-3">
          <h3 className="font-medium text-teal flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Matter Information
          </h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Title:</span> {formData.Q3}</div>
            <div><span className="text-slate-500">CCL Type:</span> {matterType?.label}</div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Matter Ref:</span> 
              <span className="font-mono font-bold text-teal">{formData.Q4}</span>
            </div>
            {formData.sharepoint_matter_type && (
              <div><span className="text-slate-500">Practice Area:</span> {formData.sharepoint_matter_type}</div>
            )}
          </div>
        </div>

        {/* Other Side Summary */}
        <div className="p-4 bg-navy-hover/60 rounded-md space-y-3">
          <h3 className="font-medium text-teal flex items-center gap-2">
            <Users className="w-4 h-4" />
            Other Side
          </h3>
          <div className="space-y-2 text-sm">
            {formData.no_counterparty ? (
              <div className="text-slate-500 italic">No counterparty (pure advisory)</div>
            ) : formData.other_side_parties.length > 0 ? (
              formData.other_side_parties.map((party, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  {party.party_type === "Company" ? (
                    <Building2 className="w-3 h-3 text-slate-400" />
                  ) : (
                    <User className="w-3 h-3 text-slate-400" />
                  )}
                  <span>{party.legal_name}</span>
                  <span className="text-slate-400">({party.party_type})</span>
                </div>
              ))
            ) : (
              <div className="text-status-red">No parties specified</div>
            )}
          </div>
        </div>

        {/* Fee Earner */}
        <div className="p-4 bg-navy-hover/60 rounded-md space-y-3">
          <h3 className="font-medium text-teal flex items-center gap-2">
            <User className="w-4 h-4" />
            Fee Earner
          </h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Name:</span> {formData.Q6}</div>
            <div><span className="text-slate-500">Email:</span> {feeEarner?.email}</div>
            <div><span className="text-slate-500">Position:</span> {feeEarner?.position}</div>
          </div>
        </div>

        {/* Pricing */}
        <div className="p-4 bg-navy-hover/60 rounded-md space-y-3 md:col-span-2">
          <h3 className="font-medium text-teal flex items-center gap-2">
            <PoundSterling className="w-4 h-4" />
            Pricing
          </h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Model:</span> {formData.Q13}</div>
            {formData.Q14 && <div><span className="text-slate-500">Fee:</span> £{formData.Q14}</div>}
            {formData.Q16 && <div><span className="text-slate-500">Disbursements:</span> {formData.Q16}</div>}
          </div>
        </div>
      </div>

      {/* Other / General CCL specific review */}
      {formData.Q9 === "other_general" && (
        <div className="p-4 bg-teal-dim/50 rounded-md space-y-3 border border-teal/50">
          <h3 className="font-medium text-teal flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Other / General CCL Details
          </h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Fee Earner Position:</span> {formData.Q30_fee_earner_position || "Not specified"}</div>
            <div><span className="text-slate-500">Backup Staff Position:</span> {formData.Q32_backup_staff_position || "Not specified"}</div>
            <div><span className="text-slate-500">Backup Staff Email:</span> {formData.Q31_backup_staff_email || "Not specified"}</div>
            <div>
              <span className="text-slate-500">General Description of Work:</span>
              <p className="mt-1 text-txt whitespace-pre-wrap bg-white p-2 rounded border">{formData.Q28_general_description || "Not specified"}</p>
            </div>
            <div><span className="text-slate-500">Legal Work Label:</span> {formData.Q29_legal_work || formData.Q3 || "Not specified"}</div>
          </div>
        </div>
      )}

      {/* Scope Preview */}
      <div className="p-4 bg-navy-hover/60 rounded-md space-y-3">
        <h3 className="font-medium text-teal flex items-center gap-2">
          <Scale className="w-4 h-4" />
          Scope of Services
        </h3>
        <div className="max-h-48 overflow-y-auto">
          <pre className="text-sm text-txt-muted whitespace-pre-wrap font-sans">
            {scopeText || "No scope defined"}
          </pre>
        </div>
      </div>

      {canSubmit && (
        <div className="p-4 bg-teal/5 rounded-md border border-teal/10">
          <p className="text-sm text-teal">
            <strong>What happens next:</strong> Clicking "Submit & Generate Letter" will create a new matter record, 
            generate a Client Care Letter based on your inputs, and send notification emails to clientcare@bonsai.law 
            and the assigned fee earner.
          </p>
        </div>
      )}
    </div>
  );
}
